import { action, computed, observable, makeObservable } from 'mobx';
import { assertNever } from 'src_common/common/assertNever';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import { RacingFilterGroupState } from 'src/domains/sportsbook/webview/components/racingFilters/RacingFilterGroupState';
import { EventsCollectionList } from 'src/domains/sportsbook/state/eventsCollection/EventsCollectionList';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { EventsCollectionState } from 'src/domains/sportsbook/shared/Types';
import { ModelsState } from 'src_common/common/websocket2/ModelsState';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { Common } from 'src/domains/common/Common';
import { LanguagesState } from 'src/domains/layouts/state/languagesState/LanguagesState';

const getEventsCollection = (
    eventsCollection: EventsCollectionState,
    models: ModelsState,
    sport: RaceSportType
): EventsCollectionList => {
    switch (sport) {
        case 'horseracing': {
            const races = eventsCollection.getRaces('horseracing');

            return races.filterBySportAndEvent((_sport, event): boolean => {
                const eventModel = models.getEvent(event.id);
                if (eventModel === null) {
                    return false;
                }
                return eventModel.tagsFootballSpecial !== 'yes';
            });
        }
        case 'greyhoundracing': {
            return eventsCollection.getRaces('greyhoundracing');
        }
        default: {
            return assertNever('RacesSlider -> eventsCollection', sport);
        }
    }
};

class RacesSliderData {
    public readonly eventsFilterState: RacingFilterGroupState;
    @observable private isFirstLoadCompeted: boolean = false;
    private readonly configComponents: ConfigComponents;

    public constructor(
        private readonly selectListAndResetOffset: (listType: SelectedListType) => void,
        private readonly sport: RaceSportType,
        private readonly getSelectedList: () => SelectedListType,
        private readonly common: Common
    ) {
        makeObservable(this);
        this.eventsFilterState = new RacingFilterGroupState(
            (): EventsCollectionList => this.eventsCollection,
            this.selectListAndResetOffset,
            LanguagesState.get(common)
        );
        this.configComponents = ConfigComponents.get(common);

        setTimeout(
            action(() => {
                this.isFirstLoadCompeted = true;
            }),
            1000
        );
    }

    @computed public get isLoadingEventsCollectionEarly(): boolean {
        let marketsLoading: number = 0;

        for (const id of this.eventsCollection.ids2) {
            id.getEventModel();
            const event = id.getEventModel();

            if (event === null) {
                continue;
            } else {
                const marketRaceWinner = event.marketRaceWinner[0]?.id2 ?? null;

                if (marketRaceWinner) {
                    const marketModel = marketRaceWinner.getModel();

                    if (marketModel === null || marketModel === undefined) {
                        marketsLoading++;
                    }
                }
            }
        }

        return marketsLoading > 0;
    }

    @computed public get isFirstHRLoadingCompleted(): boolean {
        if (this.configComponents.config.optimizeHRCarousel) {
            return this.isFirstLoadCompeted && this.isLoadingEventsCollectionEarly === false;
        }
        return true;
    }

    @computed public get eventsCollection(): EventsCollectionList {
        return getEventsCollection(EventsCollectionState.get(this.common), this.common.models, this.sport);
    }
    @computed public get listKey(): string {
        const filterId = this.eventsFilterState.todayFilterStrategy.getActiveFilterId() ?? 0;
        return `${this.sport}-${filterId.toString()}`;
    }

    @computed private get eventIds(): Array<number> {
        return this.eventsCollection.ids;
    }

    @computed public get eventsForView(): Array<EventModel> | 'loading' {
        const out = [];

        for (const id of this.eventIds) {
            const event = this.common.models.getEvent(id);

            if (event === null) {
                // Logger.captureError(`RaceCard: Could not find an event with id: ${id}`, 'internal', { id });
                continue;
            } else {
                out.push(event);
            }
        }

        const outToReturn = out
            .filter((x) => x.display && !x.timeSettingsStarted)
            .filter((eventModel) =>
                this.eventsFilterState.isEventShouldBeDisplayed(this.getSelectedList(), eventModel)
            );

        if (outToReturn.length > 2) {
            return outToReturn;
        }

        return outToReturn;
    }
}

type SelectedListType = 'all' | 'today' | 'tomorrow';
export type RaceSportType = 'horseracing' | 'greyhoundracing';

export type RacesSliderType = 'homePageWidget' | 'horseracing' | 'greyhoundracing';

export class RacesSliderState {
    private readonly data: MobxMapAutoNew<RaceSportType, RacesSliderData>;
    @observable public selectedList: SelectedListType = 'all';
    @observable public nextOffWidgetSport: RaceSportType = 'horseracing';

    public constructor(
        private readonly initialSport: RacesSliderType,
        common: Common
    ) {
        makeObservable(this);
        this.data = new MobxMapAutoNew(
            (sport: RaceSportType): RacesSliderData =>
                new RacesSliderData(this.selectListAndResetOffset, sport, () => this.selectedList, common)
        );
    }

    @computed public get eventsCollection(): RacesSliderData {
        const sport = this.racingSport;
        return this.data.get(sport);
    }

    @action public selectListAndResetOffset = (listType: SelectedListType): void => {
        this.selectedList = listType;

        if (this.eventsCollection.eventsForView.length === 0) {
            this.selectedList = 'all';
        }
    };

    @computed public get racingSport(): RaceSportType {
        if (this.initialSport === 'homePageWidget') {
            return this.nextOffWidgetSport;
        }

        return this.initialSport;
    }

    @computed public get isFirstHRLoadingCompleted(): boolean {
        return this.data.get('horseracing').isFirstHRLoadingCompleted;
    }
}
